// The select-work-steps table shell: one shared header row, then one section
// per selected audit. Two modes:
// - Browse (no search term): lazy accordion groups (WorkStepAuditGroup) that
//   fetch on first expand.
// - Search (term present): buckets from ONE batched request across every
//   selected audit (WorkStepSearchGroup) — first matches + full counts, with
//   a per-group "show all" escape into the paginated view.
// Already-imported rows show a badge and are not selectable, rather than
// being hidden.
import { Loader2 } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
} from "../../../../../components/ui/table";
import {
  OptroOpsAuditSummary,
  OptroWorkStepSearchBucket,
  OptroWorkStepSummary,
} from "../../../../../models/audit/workStep";
import { WorkStepAuditGroup } from "./WorkStepAuditGroup";
import { WorkStepSearchGroup } from "./WorkStepSearchGroup";

const COLUMN_COUNT = 5;

interface SearchModeProps {
  term: string;
  buckets: OptroWorkStepSearchBucket[];
  loading: boolean;
  error: boolean;
  retry: () => void;
}

interface SelectWorkStepsTableProps {
  audits: OptroOpsAuditSummary[];
  // Non-null while the user has a search term: the table renders search
  // buckets instead of the browse accordions.
  searchMode: SearchModeProps | null;
  isSelected: (externalRef: string) => boolean;
  onToggle: (workStep: OptroWorkStepSummary) => void;
  selectMany: (workSteps: OptroWorkStepSummary[]) => void;
  deselectMany: (externalRefs: string[]) => void;
}

export const SelectWorkStepsTable = ({
  audits,
  searchMode,
  isSelected,
  onToggle,
  selectMany,
  deselectMany,
}: SelectWorkStepsTableProps) => {
  const bucketsByAuditId = new Map(
    (searchMode?.buckets ?? []).map((bucket) => [bucket.opsAuditId, bucket]),
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10" />
          <TableHead>Work Step</TableHead>
          <TableHead>Section</TableHead>
          <TableHead>Subsection</TableHead>
          <TableHead>Preparer</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {searchMode === null ? (
          audits.map((audit) => (
            <WorkStepAuditGroup
              key={audit.id}
              audit={audit}
              search=""
              isSelected={isSelected}
              onToggle={onToggle}
              selectMany={selectMany}
              deselectMany={deselectMany}
            />
          ))
        ) : searchMode.loading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT} className="py-8 text-center">
              <Loader2 className="mx-auto h-5 w-5 animate-spin text-stone-400" />
            </TableCell>
          </TableRow>
        ) : searchMode.error ? (
          <TableRow>
            <TableCell
              colSpan={COLUMN_COUNT}
              className="py-8 text-center text-sm text-stone-500"
            >
              Could not search work steps in Optro.
              <Button
                variant="link"
                size="sm"
                className="px-1"
                onClick={searchMode.retry}
              >
                Retry
              </Button>
            </TableCell>
          </TableRow>
        ) : (
          audits.map((audit) => (
            <WorkStepSearchGroup
              key={audit.id}
              audit={audit}
              term={searchMode.term}
              bucket={bucketsByAuditId.get(audit.id)}
              isSelected={isSelected}
              onToggle={onToggle}
              selectMany={selectMany}
              deselectMany={deselectMany}
            />
          ))
        )}
      </TableBody>
    </Table>
  );
};
